import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonList,
  IonItem,
  IonLabel,
  IonSpinner,
  IonText,
  IonBadge,
  IonGrid,
  IonRow,
  IonCol,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonButton,
  IonIcon,
  IonImg,
  IonModal,
} from '@ionic/react';
import { useParams, useHistory } from 'react-router-dom';
import { 
  hourglassOutline, 
  scanOutline, 
  checkmarkCircleOutline, 
  searchOutline, 
  linkOutline, 
  documentTextOutline, 
  checkmarkDoneOutline, 
  closeCircleOutline,
  closeOutline,
  refreshOutline
} from 'ionicons/icons';
import getApi from '../api';
import EnvironmentBadge from '../components/EnvironmentBadge';
import './IncomingGoodsDetail.css';

interface DeliveryNoteItem {
  item_id: string;
  description: string;
  quantity: number;
  unit: string;
}

interface AnalyzedData {
  supplier: string;
  delivery_note_number: string;
  date: string;
  items: DeliveryNoteItem[];
}

interface DeliveryNoteDetail {
  id: number;
  status: string;
  status_display: string;
  image: string;
  analyzed_data: AnalyzedData | null;
  matched_purchase_order_id: number | null;
  fortnox_goods_receipt_id: number | null;
  items: any[];
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

const IncomingGoodsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [deliveryNote, setDeliveryNote] = useState<DeliveryNoteDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const history = useHistory();
  const api = getApi();
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = React.useRef(0);

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      'pending': 'primary',
      'analyzing': 'secondary',
      'analyzed': 'tertiary',
      'matching': 'warning',
      'matched': 'success',
      'creating_goods_receipt': 'warning',
      'completed': 'success',
      'failed': 'danger',
      'manually_corrected': 'medium'
    };
    return statusColors[status] || 'medium';
  };

  const getStatusIcon = (status: string): string => {
    const statusIcons: { [key: string]: string } = {
      'pending': hourglassOutline,
      'analyzing': scanOutline,
      'analyzed': checkmarkCircleOutline,
      'matching': searchOutline,
      'matched': linkOutline,
      'creating_goods_receipt': documentTextOutline,
      'completed': checkmarkDoneOutline,
      'failed': closeCircleOutline,
      'manually_corrected': checkmarkCircleOutline
    };
    return statusIcons[status] || hourglassOutline;
  };

  const isFinalStatus = (status: string): boolean => {
    return ['completed', 'failed', 'manually_corrected'].includes(status);
  };

  const loadDeliveryNote = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await api.get(`/fortnox/delivery-notes/${id}/`);
      console.log('Delivery note details:', response.data);
      setDeliveryNote(response.data);

      // Start polling if status is not final
      if (!isFinalStatus(response.data.status)) {
        startPolling();
      }
    } catch (error: any) {
      console.error('Failed to load delivery note details', error);
      setError('Det gick inte att ladda följesedelsinformation');
    } finally {
      setIsLoading(false);
    }
  };

  const startPolling = () => {
    if (isPolling) return;
    
    setIsPolling(true);
    pollCountRef.current = 0;

    pollingIntervalRef.current = setInterval(async () => {
      pollCountRef.current += 1;

      // Stop after 60 attempts (2 minutes)
      if (pollCountRef.current >= 60) {
        stopPolling();
        return;
      }

      try {
        const response = await api.get(`/fortnox/delivery-notes/${id}/`);
        setDeliveryNote(response.data);

        // Stop polling if final status reached
        if (isFinalStatus(response.data.status)) {
          stopPolling();
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling even on error
      }
    }, 2000); // Poll every 2 seconds
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    pollCountRef.current = 0;
  };

  useEffect(() => {
    loadDeliveryNote();

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [id]);

  const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    loadDeliveryNote().then(() => {
      event.detail.complete();
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleManualRefresh = () => {
    loadDeliveryNote();
  };

  const handlePurchaseOrderClick = () => {
    if (deliveryNote?.matched_purchase_order_id) {
      // Navigate to purchase order detail if you have that route
      // history.push(`/PurchaseOrder/${deliveryNote.matched_purchase_order_id}`);
      console.log('Navigate to PO:', deliveryNote.matched_purchase_order_id);
    }
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton defaultHref="/IncomingGoods" />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Följesedel #{id}</IonTitle>
          <div slot="end" style={{ paddingRight: '8px' }}>
            <EnvironmentBadge />
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonRefresherContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
        </IonRefresher>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            <p>Laddar följesedel...</p>
          </div>
        ) : error ? (
          <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonText color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <p>{error}</p>
              </IonText>
            </IonCardContent>
          </IonCard>
        ) : deliveryNote ? (
          <>
            {/* Status Card */}
            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <IonBadge 
                    color={getStatusColor(deliveryNote.status)} 
                    style={{ fontSize: '1rem', padding: '8px 16px' }}
                    placeholder={undefined} 
                    onPointerEnterCapture={undefined} 
                    onPointerLeaveCapture={undefined}
                  >
                    <IonIcon icon={getStatusIcon(deliveryNote.status)} style={{ marginRight: '8px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    {deliveryNote.status_display}
                  </IonBadge>
                  
                  {isPolling && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                      <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <small>Uppdaterar...</small>
                      </IonText>
                    </div>
                  )}
                  
                  {!isPolling && !isFinalStatus(deliveryNote.status) && (
                    <IonButton size="small" onClick={handleManualRefresh} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonIcon slot="icon-only" icon={refreshOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    </IonButton>
                  )}
                </div>

                <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <p>Uppladdad: {formatDate(deliveryNote.created_at)}</p>
                  {deliveryNote.updated_at !== deliveryNote.created_at && (
                    <p>Uppdaterad: {formatDate(deliveryNote.updated_at)}</p>
                  )}
                </IonText>
              </IonCardContent>
            </IonCard>

            {/* Image Card */}
            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Följesedelsbild</IonCardTitle>
              </IonCardHeader>
              <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {deliveryNote.image ? (
                  <div style={{ cursor: 'pointer' }} onClick={() => setShowImageModal(true)}>
                    <IonImg 
                      src={deliveryNote.image} 
                      alt="Följesedel" 
                      style={{ width: '100%', borderRadius: '8px' }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <p style={{ textAlign: 'center', marginTop: '8px' }}>
                        <small>Klicka för att förstora</small>
                      </p>
                    </IonText>
                  </div>
                ) : (
                  <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <p>Ingen bild tillgänglig</p>
                  </IonText>
                )}
              </IonCardContent>
            </IonCard>

            {/* Analyzed Data Card */}
            {deliveryNote.analyzed_data && (
              <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Analyserad information</IonCardTitle>
                </IonCardHeader>
                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonGrid placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonCol size="6" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <p style={{ fontSize: '0.85rem', marginBottom: '4px' }}>Leverantör</p>
                        </IonText>
                        <p><strong>{deliveryNote.analyzed_data.supplier || '-'}</strong></p>
                      </IonCol>
                      <IonCol size="6" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <p style={{ fontSize: '0.85rem', marginBottom: '4px' }}>Följesedelsnummer</p>
                        </IonText>
                        <p><strong>{deliveryNote.analyzed_data.delivery_note_number || '-'}</strong></p>
                      </IonCol>
                    </IonRow>
                    <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonCol placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <p style={{ fontSize: '0.85rem', marginBottom: '4px' }}>Datum</p>
                        </IonText>
                        <p><strong>{deliveryNote.analyzed_data.date || '-'}</strong></p>
                      </IonCol>
                    </IonRow>
                  </IonGrid>

                  {deliveryNote.analyzed_data.items && deliveryNote.analyzed_data.items.length > 0 && (
                    <>
                      <IonText placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <h3 style={{ marginTop: '16px', marginBottom: '8px' }}>Artiklar</h3>
                      </IonText>
                      <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        {deliveryNote.analyzed_data.items.map((item, index) => (
                          <IonItem key={index} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                              <h3><strong>{item.description}</strong></h3>
                              <p>Artikel: {item.item_id}</p>
                              <p>Kvantitet: {item.quantity} {item.unit}</p>
                            </IonLabel>
                          </IonItem>
                        ))}
                      </IonList>
                    </>
                  )}
                </IonCardContent>
              </IonCard>
            )}

            {/* Matched Purchase Order */}
            {deliveryNote.matched_purchase_order_id && (
              <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Matchad inköpsorder</IonCardTitle>
                </IonCardHeader>
                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonButton expand="block" onClick={handlePurchaseOrderClick} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonIcon slot="start" icon={linkOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    Visa inköpsorder #{deliveryNote.matched_purchase_order_id}
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            {/* Fortnox Goods Receipt */}
            {deliveryNote.fortnox_goods_receipt_id && (
              <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inleverans skapad</IonCardTitle>
                </IonCardHeader>
                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonText placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <p>Inleverans #{deliveryNote.fortnox_goods_receipt_id} har skapats i Fortnox</p>
                  </IonText>
                  <IonBadge color="success" style={{ marginTop: '8px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonIcon icon={checkmarkDoneOutline} style={{ marginRight: '4px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    Registrerad
                  </IonBadge>
                </IonCardContent>
              </IonCard>
            )}

            {/* Error Message */}
            {deliveryNote.error_message && (
              <IonCard color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Fel uppstod</IonCardTitle>
                </IonCardHeader>
                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonText placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <p>{deliveryNote.error_message}</p>
                  </IonText>
                </IonCardContent>
              </IonCard>
            )}

            {/* Items */}
            {deliveryNote.items && deliveryNote.items.length > 0 && (
              <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Registrerade artiklar</IonCardTitle>
                </IonCardHeader>
                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    {deliveryNote.items.map((item, index) => (
                      <IonItem key={index} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <p>{JSON.stringify(item)}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}
          </>
        ) : null}

        {/* Image Modal */}
        <IonModal isOpen={showImageModal} onDidDismiss={() => setShowImageModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonToolbar placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Följesedel</IonTitle>
              <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonButton onClick={() => setShowImageModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonIcon slot="icon-only" icon={closeOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {deliveryNote?.image && (
              <IonImg 
                src={deliveryNote.image} 
                alt="Följesedel" 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            )}
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default IncomingGoodsDetail;
